/**
 * HALO Virtual Office Platform - Supabase Client
 *
 * Handles database operations and real-time subscriptions
 * for the Manager Dashboard
 */

import { createClient, RealtimeChannel } from "@supabase/supabase-js";
import type { EmployeeWellnessData } from "../components/EmployeeCard";
import { retryWithBackoff } from "./resilience/retry";
import { circuitBreakerManager } from "./resilience/circuitBreaker";

// ============================================================================
// DATABASE TYPES
// ============================================================================

import { WELLNESS_DEFAULTS, EMOTION_SCORES } from "./constants";

export type EmotionState =
  | "Focused"
  | "Happy"
  | "Engaged"
  | "Neutral"
  | "Confused"
  | "Tired"
  | "Stressed"
  | "Frustrated";

export type HandActivity =
  | "Typing"
  | "Mouse"
  | "Gesturing"
  | "Idle"
  | "Phone Detected";

export interface WellnessLogRow {
  id: string;
  user_id: string;
  organization_id: string;
  session_id: string;
  emotion_state: EmotionState;
  emotion_confidence: number;
  posture_score: number;
  posture_details: {
    shoulder_alignment: number;
    head_tilt_angle: number;
    spine_curvature: number;
  };
  hand_activity: HandActivity;
  hand_activity_details: {
    left_hand_detected: boolean;
    right_hand_detected: boolean;
    hand_position_zone: string;
    finger_velocity: number;
  };
  fatigue_index: number;
  fatigue_details: {
    blink_rate_per_minute: number;
    average_blink_duration_ms: number;
    prolonged_closures_count: number;
    yawn_detected: boolean;
  };
  attention_metrics: {
    eye_contact_percent: number;
    gaze_direction: "center" | "left" | "right" | "up" | "down";
    gaze_stability_score: number;
    screen_focus_duration_sec: number;
  };
  device_id: string;
  timestamp: string;
  processing_time_ms: number;
  created_at: string;
}

export interface UserRow {
  id: string;
  organization_id: string;
  full_name: string;
  email: string;
  role: "admin" | "manager" | "employee";
  avatar_url: string | null;
  department: string | null;
  is_active: boolean;
  privacy_settings: {
    share_emotion: boolean;
    share_posture: boolean;
    share_fatigue: boolean;
    share_activity: boolean;
    blur_avatar: boolean;
  };
  created_at: string;
  updated_at: string;
}

export interface WellnessAlertRow {
  id: string;
  user_id: string;
  organization_id: string;
  alert_type:
    | "high_fatigue"
    | "poor_posture"
    | "extended_stress"
    | "phone_detected"
    | "prolonged_idle";
  severity: "info" | "warning" | "critical";
  message: string;
  metadata: Record<string, unknown>;
  acknowledged: boolean;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  created_at: string;
}

export interface OrganizationRow {
  id: string;
  name: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreferencesRow {
  id: string;
  user_id: string;
  wellness_alerts: boolean;
  team_activity: boolean;
  daily_summary: boolean;
  break_reminders: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  created_at: string;
  updated_at: string;
}

export interface Database {
  public: {
    Tables: {
      wellness_logs: {
        Row: WellnessLogRow;
        Insert: Omit<WellnessLogRow, "id" | "created_at">;
        Update: Partial<WellnessLogRow>;
      };
      users: {
        Row: UserRow;
        Insert: Omit<UserRow, "created_at" | "updated_at">;
        Update: Partial<UserRow>;
      };
      wellness_alerts: {
        Row: WellnessAlertRow;
        Insert: Omit<WellnessAlertRow, "id" | "created_at">;
        Update: Partial<WellnessAlertRow>;
      };
      organizations: {
        Row: OrganizationRow;
        Insert: Omit<OrganizationRow, "id" | "created_at" | "updated_at">;
        Update: Partial<OrganizationRow>;
      };
      notification_preferences: {
        Row: NotificationPreferencesRow;
        Insert: Omit<
          NotificationPreferencesRow,
          "id" | "created_at" | "updated_at"
        >;
        Update: Partial<NotificationPreferencesRow>;
      };
    };
    Functions: {
      get_user_session_summaries: {
        Args: {
          p_user_id: string;
          p_days: number;
        };
        Returns: {
          session_id: string;
          start_time: string;
          end_time: string;
          duration_minutes: number;
          avg_posture: number;
          avg_fatigue: number;
          avg_wellness: number;
          dominant_emotion: string;
          log_count: number;
        }[];
      };
    };
  };
}

// ============================================================================
// SUPABASE CLIENT
// ============================================================================

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    "[Supabase] Missing environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY",
  );
}

export const supabase = createClient<Database>(
  supabaseUrl || "http://localhost:54321",
  supabaseAnonKey || "your-anon-key",
);

// ============================================================================
// REAL-TIME SUBSCRIPTION MANAGER
// ============================================================================

type WellnessUpdateCallback = (data: EmployeeWellnessData) => void;
type AlertCallback = (alert: WellnessAlertRow) => void;
type PresenceCallback = (onlineUserIds: Set<string>) => void;

interface PresenceState {
  user_id: string;
  organization_id: string;
  status: "monitoring" | "idle" | "paused" | "offline";
  device_id: string;
  joined_at: string;
  last_seen_at?: string;
}

class RealtimeManager {
  private wellnessChannel: RealtimeChannel | null = null;
  private alertChannel: RealtimeChannel | null = null;
  private presenceChannel: RealtimeChannel | null = null;
  private userCache: Map<string, UserRow> = new Map();
  private onlineUsers: Set<string> = new Set();

  /**
   * Subscribe to real-time wellness updates for an organization
   *
   * Listens to INSERT events on the wellness_logs table
   * and transforms them into EmployeeWellnessData format
   */
  subscribeToWellnessUpdates(
    organizationId: string,
    onUpdate: WellnessUpdateCallback,
  ): () => void {
    // Unsubscribe from previous channel if exists
    this.unsubscribeWellness();

    this.wellnessChannel = supabase
      .channel(`wellness:${organizationId}`)
      .on<WellnessLogRow>(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "wellness_logs",
          filter: `organization_id=eq.${organizationId}`,
        },
        async (payload) => {
          const log = payload.new;

          // Get user info from cache or fetch
          let user = this.userCache.get(log.user_id);
          if (!user) {
            const { data } = await supabase
              .from("users")
              .select("*")
              .eq("id", log.user_id)
              .single();

            if (data) {
              user = data;
              this.userCache.set(log.user_id, data);
            }
          }

          if (!user) return;

          // Check privacy settings
          const privacy = user.privacy_settings;

          // Track which fields are hidden due to privacy settings
          const privacyHidden = {
            emotion: !privacy.share_emotion,
            posture: !privacy.share_posture,
            fatigue: !privacy.share_fatigue,
            activity: !privacy.share_activity,
          };

          // Transform to EmployeeWellnessData
          const wellnessData: EmployeeWellnessData = {
            id: user.id,
            name: user.full_name,
            email: user.email,
            avatar_url: privacy.blur_avatar
              ? undefined
              : user.avatar_url || undefined,
            department: user.department || undefined,
            emotion_state:
              privacy.share_emotion && log.emotion_state
                ? log.emotion_state
                : (WELLNESS_DEFAULTS.emotionState as any),
            emotion_confidence:
              privacy.share_emotion && log.emotion_confidence
                ? log.emotion_confidence
                : WELLNESS_DEFAULTS.emotionConfidence,
            posture_score:
              privacy.share_posture && log.posture_score !== null
                ? log.posture_score
                : WELLNESS_DEFAULTS.postureScore,
            hand_activity: privacy.share_activity ? log.hand_activity : "Idle",
            fatigue_index:
              privacy.share_fatigue && log.fatigue_index !== null
                ? log.fatigue_index
                : WELLNESS_DEFAULTS.fatigueIndex,
            fatigue_details: privacy.share_fatigue
              ? log.fatigue_details
              : {
                  blink_rate_per_minute: 0,
                  average_blink_duration_ms: 0,
                  prolonged_closures_count: 0,
                  yawn_detected: false,
                },
            attention_metrics: log.attention_metrics,
            privacy_hidden: privacyHidden,
            is_online: true,
            last_updated: new Date(log.timestamp),
          };

          onUpdate(wellnessData);
        },
      )
      .subscribe((status) => {
        console.log(`[Realtime] Wellness subscription status: ${status}`);
      });

    return () => this.unsubscribeWellness();
  }

  /**
   * Subscribe to real-time alerts for an organization
   */
  subscribeToAlerts(
    organizationId: string,
    onAlert: AlertCallback,
  ): () => void {
    this.unsubscribeAlerts();

    this.alertChannel = supabase
      .channel(`alerts:${organizationId}`)
      .on<WellnessAlertRow>(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "wellness_alerts",
          filter: `organization_id=eq.${organizationId}`,
        },
        (payload) => {
          onAlert(payload.new);
        },
      )
      .subscribe((status) => {
        console.log(`[Realtime] Alerts subscription status: ${status}`);
      });

    return () => this.unsubscribeAlerts();
  }

  /**
   * Subscribe to presence updates for an organization
   * Tracks which users are currently online in their desktop apps
   */
  subscribeToPresence(
    organizationId: string,
    onPresenceChange: PresenceCallback,
    onWellnessUpdate?: WellnessUpdateCallback,
  ): () => void {
    this.unsubscribePresence();

    const channelName = `presence:${organizationId}`;
    console.log("[Realtime] Subscribing to presence channel:", channelName);

    this.presenceChannel = supabase.channel(channelName);

    this.presenceChannel
      .on("presence", { event: "sync" }, () => {
        const state = this.presenceChannel?.presenceState();
        console.log("[Realtime] Presence sync:", state);

        // Extract all online user IDs from presence state
        // IMPORTANT: Only consider users with non-offline status
        this.onlineUsers.clear();
        if (state) {
          Object.values(state).forEach((presences) => {
            presences.forEach((p) => {
              const presence = p as unknown as PresenceState;
              // Only mark as online if status is NOT "offline"
              if (
                presence.user_id &&
                presence.status !== "offline" &&
                ["monitoring", "idle", "paused"].includes(presence.status)
              ) {
                this.onlineUsers.add(presence.user_id);
              }
            });
          });
        }

        console.log(
          "[Realtime] Online users after sync:",
          Array.from(this.onlineUsers),
        );
        onPresenceChange(new Set(this.onlineUsers));
      })
      .on("presence", { event: "join" }, ({ key, newPresences }) => {
        console.log("[Realtime] User joined:", key, newPresences);
        newPresences.forEach((p) => {
          const presence = p as unknown as PresenceState;
          if (
            presence.user_id &&
            presence.status !== "offline" &&
            ["monitoring", "idle", "paused"].includes(presence.status)
          ) {
            this.onlineUsers.add(presence.user_id);
          }
        });
        onPresenceChange(new Set(this.onlineUsers));
      })
      .on("presence", { event: "leave" }, ({ key, leftPresences }) => {
        console.log("[Realtime] User left:", key, leftPresences);
        leftPresences.forEach((p) => {
          const presence = p as unknown as PresenceState;
          if (presence.user_id) {
            this.onlineUsers.delete(presence.user_id);
          }
        });
        onPresenceChange(new Set(this.onlineUsers));
      })
      .on("broadcast", { event: "wellness_update" }, ({ payload }) => {
        console.log("[Realtime] Received wellness broadcast:", payload);
        if (onWellnessUpdate && payload) {
          // Map broadcast payload (WellnessLog) to EmployeeWellnessData structure
          // Desktop sends WellnessLog, we need to ensure it's compatible
          const broadcastData = payload as any;
          const wellnessData: Partial<EmployeeWellnessData> = {
            id: broadcastData.user_id, // Map user_id to id
            emotion_state: broadcastData.emotion_state,
            emotion_confidence: broadcastData.emotion_confidence,
            posture_score: broadcastData.posture_score,
            hand_activity: broadcastData.hand_activity,
            fatigue_index: broadcastData.fatigue_index,
            fatigue_details: broadcastData.fatigue_details,
            attention_metrics: broadcastData.attention_metrics,
            is_online: true,
            last_updated: new Date(broadcastData.timestamp), // Convert string to Date
          };
          onWellnessUpdate(wellnessData as EmployeeWellnessData);
        }
      })
      .subscribe((status) => {
        console.log(`[Realtime] Presence subscription status: ${status}`);

        // When subscribed, immediately check current presence state
        // This handles the case where users joined before we subscribed
        if (status === "SUBSCRIBED") {
          console.log(
            "[Realtime] Subscription confirmed, checking current presence state",
          );
          const state = this.presenceChannel?.presenceState();
          if (state && Object.keys(state).length > 0) {
            console.log("[Realtime] Initial presence state found:", state);
            // Trigger sync event manually to process existing presences
            this.onlineUsers.clear();
            Object.values(state).forEach((presences) => {
              presences.forEach((p) => {
                const presence = p as unknown as PresenceState;
                if (
                  presence.user_id &&
                  presence.status !== "offline" &&
                  ["monitoring", "idle", "paused"].includes(presence.status)
                ) {
                  this.onlineUsers.add(presence.user_id);
                }
              });
            });
            console.log(
              "[Realtime] Online users after initial sync:",
              Array.from(this.onlineUsers),
            );
            onPresenceChange(new Set(this.onlineUsers));
          } else {
            console.log("[Realtime] No initial presence state found");
          }
        }
      });

    return () => this.unsubscribePresence();
  }

  /**
   * Get current set of online user IDs
   */
  getOnlineUsers(): Set<string> {
    return new Set(this.onlineUsers);
  }

  private unsubscribeWellness(): void {
    if (this.wellnessChannel) {
      supabase.removeChannel(this.wellnessChannel);
      this.wellnessChannel = null;
    }
  }

  private unsubscribeAlerts(): void {
    if (this.alertChannel) {
      supabase.removeChannel(this.alertChannel);
      this.alertChannel = null;
    }
  }

  private unsubscribePresence(): void {
    if (this.presenceChannel) {
      supabase.removeChannel(this.presenceChannel);
      this.presenceChannel = null;
      this.onlineUsers.clear();
    }
  }

  /**
   * Clear user cache (call when users are updated)
   */
  clearUserCache(): void {
    this.userCache.clear();
  }
}

export const realtimeManager = new RealtimeManager();

// ============================================================================
// DATA FETCHING FUNCTIONS
// ============================================================================

/**
 * Fetch all team members for an organization
 */
export async function fetchTeamMembers(
  organizationId: string,
): Promise<EmployeeWellnessData[]> {
  // Fetch users with retry logic
  const fetchUsers = async () => {
    const { data: users, error: usersError } = await supabase
      .from("users")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("is_active", true);

    if (usersError) {
      throw new Error(`Failed to fetch users: ${usersError.message}`);
    }
    return users;
  };

  let users;
  try {
    users = await retryWithBackoff(fetchUsers, {
      maxAttempts: 3,
      initialDelayMs: 500,
    });
  } catch (error) {
    console.error("[Supabase] Error fetching users:", error);
    return [];
  }

  if (!users || users.length === 0) {
    return [];
  }

  // Fetch latest wellness log for each user
  const typedUsers = users as UserRow[];
  const userIds = typedUsers.map((u) => u.id);

  // Get the most recent log per user using a window function approach
  // We'll fetch recent logs and dedupe client-side for simplicity
  const { data: logs, error: logsError } = await supabase
    .from("wellness_logs")
    .select("*")
    .in("user_id", userIds)
    .order("timestamp", { ascending: false })
    .limit(userIds.length * 2); // Get extra in case of recent activity

  if (logsError) {
    console.error("[Supabase] Error fetching logs:", logsError);
  }

  // Create a map of latest logs per user
  const latestLogs = new Map<string, WellnessLogRow>();
  if (logs) {
    const typedLogs = logs as WellnessLogRow[];
    for (const log of typedLogs) {
      if (!latestLogs.has(log.user_id)) {
        latestLogs.set(log.user_id, log);
      }
    }
  }

  // Transform to EmployeeWellnessData
  return typedUsers.map((user) => {
    const log = latestLogs.get(user.id);
    const privacy = user.privacy_settings;
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    const isOnline = log
      ? new Date(log.timestamp).getTime() > fiveMinutesAgo
      : false;

    // Track which fields are hidden due to privacy settings
    const hasData = !!log;
    const privacyHidden = {
      emotion: hasData && !privacy.share_emotion,
      posture: hasData && !privacy.share_posture,
      fatigue: hasData && !privacy.share_fatigue,
      activity: hasData && !privacy.share_activity,
    };

    return {
      id: user.id,
      name: user.full_name,
      email: user.email,
      avatar_url: privacy.blur_avatar
        ? undefined
        : user.avatar_url || undefined,
      department: user.department || undefined,
      emotion_state:
        log && privacy.share_emotion
          ? log.emotion_state
          : (WELLNESS_DEFAULTS.emotionState as any),
      emotion_confidence:
        log && privacy.share_emotion
          ? log.emotion_confidence
          : WELLNESS_DEFAULTS.emotionConfidence,
      posture_score:
        log && privacy.share_posture
          ? log.posture_score
          : WELLNESS_DEFAULTS.postureScore,
      hand_activity: log && privacy.share_activity ? log.hand_activity : "Idle",
      fatigue_index:
        log && privacy.share_fatigue
          ? log.fatigue_index
          : WELLNESS_DEFAULTS.fatigueIndex,
      fatigue_details:
        log && privacy.share_fatigue
          ? log.fatigue_details
          : {
              blink_rate_per_minute: 0,
              average_blink_duration_ms: 0,
              prolonged_closures_count: 0,
              yawn_detected: false,
            },
      attention_metrics: log?.attention_metrics || {
        eye_contact_percent: 0,
        gaze_direction: "center" as const,
        gaze_stability_score: 0,
        screen_focus_duration_sec: 0,
      },
      privacy_hidden: privacyHidden,
      is_online: isOnline,
      last_updated: log ? new Date(log.timestamp) : new Date(0), // Epoch if no log
    };
  });
}

/**
 * Fetch unacknowledged alerts for an organization
 */
export async function fetchActiveAlerts(
  organizationId: string,
): Promise<WellnessAlertRow[]> {
  const fetchAlerts = async () => {
    const { data, error } = await supabase
      .from("wellness_alerts")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("acknowledged", false)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      throw new Error(`Failed to fetch alerts: ${error.message}`);
    }
    return data || [];
  };

  try {
    return await retryWithBackoff(fetchAlerts, {
      maxAttempts: 3,
      initialDelayMs: 500,
    });
  } catch (error) {
    console.error("[Supabase] Error fetching alerts:", error);
    return [];
  }
}

/**
 * Acknowledge an alert
 */
export async function acknowledgeAlert(
  alertId: string,
  userId: string,
): Promise<boolean> {
  const updateData = {
    acknowledged: true,
    acknowledged_at: new Date().toISOString(),
    acknowledged_by: userId,
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.from("wellness_alerts") as any)
    .update(updateData)
    .eq("id", alertId);

  if (error) {
    console.error("[Supabase] Error acknowledging alert:", error);
    return false;
  }

  return true;
}

/**
 * Fetch aggregated wellness stats for an organization
 */
export async function fetchOrganizationStats(
  organizationId: string,
  periodType: "hourly" | "daily" | "weekly" = "daily",
) {
  const { data, error } = await supabase
    .from("wellness_aggregates")
    .select("*")
    .eq("organization_id", organizationId)
    .eq("period_type", periodType)
    .order("period_start", { ascending: false })
    .limit(30);

  if (error) {
    console.error("[Supabase] Error fetching stats:", error);
    return [];
  }

  return data || [];
}

// ============================================================================
// DASHBOARD KPI & CHART DATA
// ============================================================================

export interface DashboardKPIs {
  totalEmployees: number;
  activeNow: number;
  avgWellnessScore: number | null;
  activeAlerts: number;
  avgPosture: number | null;
  avgFatigue: number | null;
  highFatigueCount: number;
  poorPostureCount: number;
  // Trends (compared to previous period)
  wellnessTrend: number; // percentage change
  alertsTrend: number;
}

/**
 * Fetch live dashboard KPIs for an organization
 */
export async function fetchDashboardKPIs(
  organizationId: string,
): Promise<DashboardKPIs> {
  try {
    // Parallel fetch: users, latest logs, alerts
    const [usersResult, logsResult, alertsResult, prevAlertsResult] =
      await Promise.all([
        // Total active employees
        supabase
          .from("users")
          .select("id", { count: "exact" })
          .eq("organization_id", organizationId)
          .eq("is_active", true),

        // Recent wellness logs (last 24h for active count + averages)
        supabase
          .from("wellness_logs")
          .select(
            "user_id, posture_score, fatigue_index, emotion_state, timestamp",
          )
          .eq("organization_id", organizationId)
          .gte(
            "timestamp",
            new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          )
          .order("timestamp", { ascending: false })
          .limit(500),

        // Active (unacknowledged) alerts
        supabase
          .from("wellness_alerts")
          .select("id", { count: "exact" })
          .eq("organization_id", organizationId)
          .eq("acknowledged", false),

        // Previous period alerts (24-48h ago) for trend
        supabase
          .from("wellness_alerts")
          .select("id", { count: "exact" })
          .eq("organization_id", organizationId)
          .gte(
            "created_at",
            new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          )
          .lt(
            "created_at",
            new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          ),
      ]);

    const totalEmployees = usersResult.count || 0;
    const activeAlerts = alertsResult.count || 0;
    const prevAlerts = prevAlertsResult.count || 0;

    // Compute active users (unique user_ids with logs in last 5 min)
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    const logs = (logsResult.data as WellnessLogRow[]) || [];
    const recentUserIds = new Set(
      logs
        .filter((l) => new Date(l.timestamp).getTime() > fiveMinutesAgo)
        .map((l) => l.user_id),
    );
    const activeNow = recentUserIds.size;

    // Compute averages from latest log per user (dedupe)
    const latestPerUser = new Map<
      string,
      { posture: number; fatigue: number; emotion: string }
    >();
    for (const log of logs) {
      if (!latestPerUser.has(log.user_id)) {
        latestPerUser.set(log.user_id, {
          posture: log.posture_score,
          fatigue: log.fatigue_index,
          emotion: log.emotion_state,
        });
      }
    }

    const userMetrics = Array.from(latestPerUser.values());
    const avgPosture =
      userMetrics.length > 0
        ? Math.round(
            userMetrics.reduce((s, m) => s + m.posture, 0) / userMetrics.length,
          )
        : null;
    const avgFatigue =
      userMetrics.length > 0
        ? Math.round(
            (userMetrics.reduce((s, m) => s + m.fatigue, 0) /
              userMetrics.length) *
              100,
          ) / 100
        : null;
    const highFatigueCount = userMetrics.filter((m) => m.fatigue > 0.7).length;
    const poorPostureCount = userMetrics.filter((m) => m.posture < 50).length;

    // Composite wellness score (matches SQL calculate_wellness_score logic)
    const avgWellnessScore =
      userMetrics.length > 0
        ? Math.round(
            userMetrics.reduce((sum, m) => {
              const emotionScore = EMOTION_SCORES[m.emotion] || 50;
              return (
                sum +
                m.posture * 0.4 +
                (1 - m.fatigue) * 100 * 0.3 +
                emotionScore * 0.3
              );
            }, 0) / userMetrics.length,
          )
        : null;

    // Trend calculation
    const alertsTrend =
      prevAlerts > 0
        ? Math.round(((activeAlerts - prevAlerts) / prevAlerts) * 100)
        : 0;

    return {
      totalEmployees,
      activeNow,
      avgWellnessScore,
      activeAlerts,
      avgPosture,
      avgFatigue,
      highFatigueCount,
      poorPostureCount,
      wellnessTrend: 0, // Will be computed with aggregates later
      alertsTrend,
    };
  } catch (err) {
    console.error("[Supabase] fetchDashboardKPIs error:", err);
    return {
      totalEmployees: 0,
      activeNow: 0,
      avgWellnessScore: 0,
      activeAlerts: 0,
      avgPosture: 70,
      avgFatigue: 0,
      highFatigueCount: 0,
      poorPostureCount: 0,
      wellnessTrend: 0,
      alertsTrend: 0,
    };
  }
}

export interface WellnessChartDataPoint {
  date: string;
  avgPosture: number;
  avgFatigue: number;
  avgWellness: number;
  activeUsers: number;
}

/**
 * Fetch time-series chart data for wellness trends
 */
export async function fetchWellnessChartData(
  organizationId: string,
  days: number = 30,
): Promise<WellnessChartDataPoint[]> {
  try {
    const since = new Date(
      Date.now() - days * 24 * 60 * 60 * 1000,
    ).toISOString();

    const { data, error } = await supabase
      .from("wellness_logs")
      .select("user_id, posture_score, fatigue_index, emotion_state, timestamp")
      .eq("organization_id", organizationId)
      .gte("timestamp", since)
      .order("timestamp", { ascending: true })
      .limit(5000);

    if (error) {
      console.error("[Supabase] Error fetching chart data:", error);
      return [];
    }

    if (!data || data.length === 0) return [];

    // Group by date and compute daily averages
    const dailyMap = new Map<
      string,
      {
        postureSum: number;
        fatigueSum: number;
        wellnessSum: number;
        count: number;
        userIds: Set<string>;
      }
    >();

    const emotionScoreMap: Record<string, number> = {
      Focused: 90,
      Happy: 95,
      Engaged: 85,
      Neutral: 70,
      Confused: 50,
      Tired: 40,
      Stressed: 35,
      Frustrated: 30,
    };

    for (const log of data as WellnessLogRow[]) {
      const dateKey = new Date(log.timestamp).toISOString().split("T")[0];
      const existing = dailyMap.get(dateKey) || {
        postureSum: 0,
        fatigueSum: 0,
        wellnessSum: 0,
        count: 0,
        userIds: new Set<string>(),
      };

      const emotionScore = emotionScoreMap[log.emotion_state] || 50;
      const wellness =
        log.posture_score * 0.4 +
        (1 - log.fatigue_index) * 100 * 0.3 +
        emotionScore * 0.3;

      existing.postureSum += log.posture_score;
      existing.fatigueSum += log.fatigue_index;
      existing.wellnessSum += wellness;
      existing.count += 1;
      existing.userIds.add(log.user_id);
      dailyMap.set(dateKey, existing);
    }

    return Array.from(dailyMap.entries())
      .map(([date, agg]) => ({
        date,
        avgPosture: Math.round(agg.postureSum / agg.count),
        avgFatigue: Math.round((agg.fatigueSum / agg.count) * 100) / 100,
        avgWellness: Math.round(agg.wellnessSum / agg.count),
        activeUsers: agg.userIds.size,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  } catch (err) {
    console.error("[Supabase] fetchWellnessChartData error:", err);
    return [];
  }
}

export interface RecentActivityItem {
  id: string;
  type: "alert" | "status";
  message: string;
  time: string;
  severity: "warning" | "success" | "info" | "destructive";
  employeeName?: string;
}

/**
 * Fetch recent activity (alerts + status changes) for the dashboard feed
 */
export async function fetchRecentActivity(
  organizationId: string,
  limit: number = 10,
): Promise<RecentActivityItem[]> {
  try {
    // Fetch recent alerts (both acknowledged and unacknowledged)
    const { data: alerts, error } = await supabase
      .from("wellness_alerts")
      .select("*, users!wellness_alerts_user_id_fkey(full_name)")
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("[Supabase] Error fetching recent activity:", error);

      // Fallback: fetch without join
      const { data: alertsOnly } = await supabase
        .from("wellness_alerts")
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (!alertsOnly) return [];

      return (alertsOnly as WellnessAlertRow[]).map((alert) => {
        const severityMap: Record<string, RecentActivityItem["severity"]> = {
          critical: "destructive",
          warning: "warning",
          info: "info",
        };
        const timeAgo = formatTimeAgo(new Date(alert.created_at));

        return {
          id: alert.id,
          type: "alert" as const,
          message: alert.message,
          time: timeAgo,
          severity: severityMap[alert.severity] || "info",
        };
      });
    }

    if (!alerts || alerts.length === 0) return [];

    return alerts.map((alert: any) => {
      const severityMap: Record<string, RecentActivityItem["severity"]> = {
        critical: "destructive",
        warning: "warning",
        info: "info",
      };
      const timeAgo = formatTimeAgo(new Date(alert.created_at));
      const name = alert.users?.full_name;

      return {
        id: alert.id,
        type: "alert" as const,
        message: name ? `${name}: ${alert.message}` : alert.message,
        time: timeAgo,
        severity: severityMap[alert.severity] || "info",
        employeeName: name,
      };
    });
  } catch (err) {
    console.error("[Supabase] fetchRecentActivity error:", err);
    return [];
  }
}

/**
 * Helper: human-readable time ago string
 */
function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ============================================================================
// EMPLOYEE DETAIL DATA
// ============================================================================

/**
 * Fetch full wellness log history for a single employee.
 * Returns all JSONB detail fields (posture_details, fatigue_details, etc.)
 */
export async function fetchEmployeeWellnessHistory(
  userId: string,
  days: number = 7,
): Promise<WellnessLogRow[]> {
  try {
    const since = new Date(
      Date.now() - days * 24 * 60 * 60 * 1000,
    ).toISOString();

    const { data, error } = await supabase
      .from("wellness_logs")
      .select("*")
      .eq("user_id", userId)
      .gte("timestamp", since)
      .order("timestamp", { ascending: false })
      .limit(2000);

    if (error) {
      console.error("[Supabase] Error fetching employee history:", error);
      return [];
    }

    return (data as WellnessLogRow[]) || [];
  } catch (err) {
    console.error("[Supabase] fetchEmployeeWellnessHistory error:", err);
    return [];
  }
}

/**
 * Fetch alert history for a single employee
 */
export async function fetchEmployeeAlertHistory(
  userId: string,
  limit: number = 50,
): Promise<WellnessAlertRow[]> {
  try {
    const { data, error } = await supabase
      .from("wellness_alerts")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("[Supabase] Error fetching employee alerts:", error);
      return [];
    }

    return (data as WellnessAlertRow[]) || [];
  } catch (err) {
    console.error("[Supabase] fetchEmployeeAlertHistory error:", err);
    return [];
  }
}

/**
 * Fetch employee session summaries from the database
 * Uses the get_user_session_summaries RPC function
 */
export async function fetchEmployeeSessions(
  userId: string,
  days: number = 7,
): Promise<SessionSummary[]> {
  try {
    const { data, error } = await supabase.rpc("get_user_session_summaries", {
      p_user_id: userId,
      p_days: days,
    } as any);

    if (error) {
      console.error("[Supabase] Error fetching sessions:", error);
      return [];
    }

    // Map RPC result to SessionSummary interface
    // Note: RPC returns snake_case, interface uses camelCase
    if (!data) return [];
    return (data as any[]).map((s: any) => ({
      sessionId: s.session_id,
      startTime: s.start_time,
      endTime: s.end_time,
      durationMinutes: s.duration_minutes,
      avgPosture: s.avg_posture,
      avgFatigue: s.avg_fatigue,
      // Calculate wellness score if not returned by RPC
      avgWellness:
        s.avg_wellness ||
        Math.round(
          (s.avg_posture || 0) * 0.4 +
            (1 - (s.avg_fatigue || 0)) * 100 * 0.3 +
            (EMOTION_SCORES[s.dominant_emotion] || 50) * 0.3,
        ),
      dominantEmotion: s.dominant_emotion,
      logCount: s.log_count,
    }));
  } catch (err) {
    console.error("[Supabase] fetchEmployeeSessions error:", err);
    return [];
  }
}

export interface SessionSummary {
  sessionId: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  avgPosture: number | null;
  avgFatigue: number | null;
  avgWellness: number | null;
  dominantEmotion: string;
  logCount: number;
}

/**
 * Compute session-level summaries from wellness logs using session_id
 */
export function computeSessionSummaries(
  logs: WellnessLogRow[],
): SessionSummary[] {
  const sessionMap = new Map<string, WellnessLogRow[]>();

  for (const log of logs) {
    if (!log.session_id) continue;
    const existing = sessionMap.get(log.session_id) || [];
    existing.push(log);
    sessionMap.set(log.session_id, existing);
  }

  const emotionScoreMap: Record<string, number> = {
    Focused: 90,
    Happy: 95,
    Engaged: 85,
    Neutral: 70,
    Confused: 50,
    Tired: 40,
    Stressed: 35,
    Frustrated: 30,
  };

  return Array.from(sessionMap.entries())
    .map(([sessionId, sessionLogs]) => {
      const sorted = sessionLogs.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );
      const start = new Date(sorted[0].timestamp);
      const end = new Date(sorted[sorted.length - 1].timestamp);
      const durationMinutes = Math.round(
        (end.getTime() - start.getTime()) / 60000,
      );

      const avgPosture = Math.round(
        sorted.reduce((s, l) => s + l.posture_score, 0) / sorted.length,
      );
      const avgFatigue =
        Math.round(
          (sorted.reduce((s, l) => s + l.fatigue_index, 0) / sorted.length) *
            100,
        ) / 100;

      // Dominant emotion
      const emotionCounts = new Map<string, number>();
      sorted.forEach((l) => {
        emotionCounts.set(
          l.emotion_state,
          (emotionCounts.get(l.emotion_state) || 0) + 1,
        );
      });
      const dominantEmotion =
        Array.from(emotionCounts.entries()).sort(
          (a, b) => b[1] - a[1],
        )[0]?.[0] || "Neutral";

      const avgWellness = Math.round(
        sorted.reduce((sum, l) => {
          const emotionScore = emotionScoreMap[l.emotion_state] || 50;
          return (
            sum +
            l.posture_score * 0.4 +
            (1 - l.fatigue_index) * 100 * 0.3 +
            emotionScore * 0.3
          );
        }, 0) / sorted.length,
      );

      return {
        sessionId,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        durationMinutes: Math.max(durationMinutes, 1),
        avgPosture,
        avgFatigue,
        avgWellness,
        dominantEmotion,
        logCount: sorted.length,
      };
    })
    .sort(
      (a, b) =>
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
    );
}

/**
 * Fetch a single user profile by ID
 */
export async function fetchUserProfile(
  userId: string,
): Promise<UserRow | null> {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) {
      console.error("[Supabase] Error fetching user profile:", error);
      return null;
    }

    return data as UserRow;
  } catch (err) {
    console.error("[Supabase] fetchUserProfile error:", err);
    return null;
  }
}

// ============================================================================
// USER PROFILE MANAGEMENT
// ============================================================================

export interface EnsureProfileResult {
  success: boolean;
  user: UserRow | null;
  error: string | null;
}

/**
 * Ensures a user profile exists in public.users table
 * Creates one if it doesn't exist (triggered after auth signup/signin)
 */
export async function ensureUserProfile(
  userId: string,
  _email: string,
  _fullNameFallback?: string,
): Promise<EnsureProfileResult> {
  console.log("[Supabase] ensureUserProfile starting for:", userId);
  const startTime = Date.now();

  // Use circuit breaker for auth operations
  const fetchProfile = async () => {
    // 1. Try to fetch existing profile (should exist now due to trigger)
    console.log("[Supabase] Fetching user profile...");
    const { data: existingProfile, error: fetchError } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .single();

    console.log(
      "[Supabase] Profile fetch completed in",
      Date.now() - startTime,
      "ms",
    );

    // If profile exists, check if they have an organization
    if (existingProfile) {
      const typedProfile = existingProfile as UserRow;
      console.log(
        "[Supabase] Profile found, org_id:",
        typedProfile.organization_id,
      );
      // User has organization - all set
      if (typedProfile.organization_id) {
        return { success: true, user: typedProfile, error: null };
      }

      // Profile exists but no organization - need to create one
      return {
        success: false,
        user: null,
        error: "NEEDS_ORG_SETUP",
      };
    }

    // PGRST116 = "No rows found" - shouldn't happen with trigger, but handle it
    if (fetchError && fetchError.code !== "PGRST116") {
      console.error("[Supabase] Error fetching profile:", fetchError);
      return { success: false, user: null, error: fetchError.message };
    }

    // Profile doesn't exist (trigger failed?) - show org setup anyway
    console.warn(
      "[Supabase] No profile found for user, trigger may have failed:",
      userId,
    );
    return {
      success: false,
      user: null,
      error: "NEEDS_ORG_SETUP",
    };
  };

  try {
    return await circuitBreakerManager.execute(
      "user-profile-fetch",
      fetchProfile,
      { failureThreshold: 3, timeout: 30000 },
    );
  } catch (err: any) {
    console.error("[Supabase] ensureUserProfile error:", err);
    return {
      success: false,
      user: null,
      error: err.message || "Unknown error",
    };
  }
}

// ============================================================================
// ORGANIZATION MANAGEMENT
// ============================================================================

export interface CreateOrganizationResult {
  success: boolean;
  organization?: { id: string; name: string };
  user?: UserRow;
  error?: string;
}

/**
 * Create a new organization and set the current user as admin
 * Used during initial signup flow
 */
export async function createOrganizationWithAdmin(
  userId: string,
  _email: string,
  fullName: string,
  organizationName: string,
): Promise<CreateOrganizationResult> {
  const createOrgOperation = async () => {
    // 1. Create the organization
    const orgInsertData = {
      name: organizationName,
      settings: {
        created_at: new Date().toISOString(),
        plan: "free",
      },
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: newOrg, error: orgError } = await (
      supabase.from("organizations") as any
    )
      .insert(orgInsertData)
      .select("id, name")
      .single();

    if (orgError) {
      console.error("[Supabase] Error creating organization:", orgError);
      return {
        success: false,
        error: `Failed to create organization: ${orgError.message}`,
      };
    }

    const typedOrg = newOrg as { id: string; name: string };

    // 2. Update the user's profile with the new organization
    // (Profile was auto-created by trigger, but organization_id was NULL)
    const userUpdateData = {
      organization_id: typedOrg.id,
      full_name: fullName, // Update in case they changed it
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: updateError } = await (supabase.from("users") as any)
      .update(userUpdateData)
      .eq("id", userId);

    if (updateError) {
      console.error("[Supabase] Error updating user with org:", updateError);
      // Try to clean up the organization
      await supabase.from("organizations").delete().eq("id", typedOrg.id);
      return {
        success: false,
        error: `Failed to link user to organization: ${updateError.message}`,
      };
    }

    // 3. Fetch the updated user profile
    const { data: userProfile, error: fetchError } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .single();

    if (fetchError || !userProfile) {
      console.error("[Supabase] Error fetching updated profile:", fetchError);
      return {
        success: false,
        error: "Organization created but failed to fetch profile",
      };
    }

    const typedProfile = userProfile as UserRow;
    console.log(
      "[Supabase] Created organization and linked admin:",
      typedOrg.name,
      typedProfile.id,
    );
    return {
      success: true,
      organization: typedOrg,
      user: typedProfile,
    };
  };

  try {
    return await retryWithBackoff(createOrgOperation, {
      maxAttempts: 2,
      initialDelayMs: 1000,
    });
  } catch (err: any) {
    console.error("[Supabase] createOrganizationWithAdmin error:", err);
    return { success: false, error: err.message || "Unknown error" };
  }
}

// ============================================================================
// ADMIN FUNCTIONS - EMPLOYEE MANAGEMENT
// ============================================================================

export interface CreateEmployeeResult {
  success: boolean;
  user_id?: string;
  email?: string;
  error?: string;
}

/**
 * Admin creates a new employee account
 * Calls the Supabase Edge Function which uses service role
 */
export async function createEmployeeAccount(
  email: string,
  password: string,
  fullName: string,
  department?: string,
  role: "employee" | "manager" = "employee",
): Promise<CreateEmployeeResult> {
  const createEmployee = async () => {
    // Get fresh user and session to ensure token is valid
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      console.error("[Supabase] getUser error:", userError);
      return { success: false, error: "Not authenticated" };
    }

    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error("[Supabase] getSession error:", sessionError);
      return { success: false, error: "No valid session" };
    }

    console.log(
      "[Supabase] Creating employee with session:",
      session.user.email,
    );
    console.log(
      "[Supabase] Token preview:",
      session.access_token.substring(0, 30) + "...",
    );

    const response = await supabase.functions.invoke("create-employee", {
      body: {
        email,
        password,
        full_name: fullName,
        department,
        role,
        admin_user_id: user.id, // Include admin user ID in body for service_role fallback
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    console.log("[Supabase] Edge function response:", response);

    if (response.error) {
      console.error("[Supabase] Edge function error:", response.error);
      return { success: false, error: response.error.message };
    }

    return response.data as CreateEmployeeResult;
  };

  try {
    return await retryWithBackoff(createEmployee, {
      maxAttempts: 2,
      initialDelayMs: 1000,
    });
  } catch (err: any) {
    console.error("[Supabase] createEmployeeAccount error:", err);
    return { success: false, error: err.message || "Unknown error" };
  }
}

/**
 * Get all employees in the admin's organization
 */
export async function getOrganizationEmployees(): Promise<UserRow[]> {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[Supabase] Error fetching employees:", error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error("[Supabase] getOrganizationEmployees error:", err);
    return [];
  }
}

/**
 * Update an employee's details
 */
export async function updateEmployee(
  employeeId: string,
  updates: {
    full_name?: string;
    role?: "employee" | "manager";
    is_active?: boolean;
    department?: string;
  },
): Promise<{ success: boolean; error?: string }> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase.from("users") as any)
      .update(updates)
      .eq("id", employeeId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Delete an employee account
 * Calls the Supabase Edge Function which uses service role to delete auth user
 */
export interface DeleteEmployeeResult {
  success: boolean;
  error?: string;
}

export async function deleteEmployee(
  employeeId: string,
): Promise<DeleteEmployeeResult> {
  const deleteEmployeeOp = async () => {
    // Get fresh session to ensure token is valid
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session) {
      console.error("[Supabase] getSession error:", sessionError);
      return { success: false, error: "No valid session" };
    }

    // Fix: Use direct fetch instead of supabase.functions.invoke to avoid network errors
    // The invoke method can fail with "failed to send request" in some browser/edge cases
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const functionUrl = `${supabaseUrl}/functions/v1/delete-employee`;

    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        employee_id: employeeId,
      }),
    });

    console.log("[Supabase] Delete employee edge function response status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[Supabase] Delete employee edge function error:", errorText);
      let errorMessage = `Failed to delete employee (${response.status})`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorMessage;
      } catch {
        // Use default error message
      }
      return { success: false, error: errorMessage };
    }

    const data = await response.json();
    console.log("[Supabase] Delete employee success:", data);
    return data as DeleteEmployeeResult;
  };

  try {
    return await retryWithBackoff(deleteEmployeeOp, {
      maxAttempts: 2,
      initialDelayMs: 1000,
    });
  } catch (err: any) {
    console.error("[Supabase] deleteEmployee error:", err);
    return { success: false, error: err.message || "Unknown error" };
  }
}

/**
 * Get organization details
 */
export async function getOrganization(
  orgId: string,
): Promise<OrganizationRow | null> {
  try {
    const { data, error } = await supabase
      .from("organizations")
      .select("*")
      .eq("id", orgId)
      .single();

    if (error) {
      console.error("[Supabase] Error fetching organization:", error);
      return null;
    }

    return data as OrganizationRow;
  } catch (err) {
    console.error("[Supabase] getOrganization error:", err);
    return null;
  }
}

/**
 * Get unique departments from organization employees
 */
export async function getOrganizationDepartments(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("department")
      .not("department", "is", null);

    if (error) {
      console.error("[Supabase] Error fetching departments:", error);
      return [];
    }

    // Extract unique department names
    const departments = new Set<string>();
    (data as Array<{ department: string | null }>)?.forEach((user) => {
      if (user.department) {
        departments.add(user.department);
      }
    });

    return Array.from(departments).sort();
  } catch (err) {
    console.error("[Supabase] getOrganizationDepartments error:", err);
    return [];
  }
}

// ============================================================================
// USER SETTINGS & PREFERENCES
// ============================================================================

export interface PrivacySettings {
  share_emotion: boolean;
  share_posture: boolean;
  share_fatigue: boolean;
  share_activity: boolean;
  blur_avatar: boolean;
}

export interface NotificationPreferences {
  wellness_alerts: boolean;
  team_activity: boolean;
  daily_summary: boolean;
  break_reminders: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
}

export interface UpdateProfileResult {
  success: boolean;
  user?: UserRow;
  error?: string;
}

/**
 * Update current user's profile
 */
export async function updateUserProfile(
  userId: string,
  updates: { full_name?: string; department?: string },
): Promise<UpdateProfileResult> {
  try {
    const { data, error } = await (supabase.from("users") as any)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) {
      console.error("[Supabase] Error updating profile:", error);
      return { success: false, error: error.message };
    }

    return { success: true, user: data as UserRow };
  } catch (err: any) {
    console.error("[Supabase] updateUserProfile error:", err);
    return { success: false, error: err.message };
  }
}

/**
 * Change user password
 */
export async function changePassword(
  _currentPassword: string,
  newPassword: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    // Update the password using Supabase Auth
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      console.error("[Supabase] Error changing password:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error("[Supabase] changePassword error:", err);
    return { success: false, error: err.message };
  }
}

/**
 * Update user privacy settings
 */
export async function updatePrivacySettings(
  userId: string,
  settings: PrivacySettings,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await (supabase.from("users") as any)
      .update({
        privacy_settings: settings,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId);

    if (error) {
      console.error("[Supabase] Error updating privacy settings:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error("[Supabase] updatePrivacySettings error:", err);
    return { success: false, error: err.message };
  }
}

/**
 * Get user privacy settings
 */
export async function getPrivacySettings(
  userId: string,
): Promise<PrivacySettings | null> {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("privacy_settings")
      .eq("id", userId)
      .single();

    if (error || !data) {
      console.error("[Supabase] Error fetching privacy settings:", error);
      return null;
    }

    return (data as any).privacy_settings as PrivacySettings;
  } catch (err) {
    console.error("[Supabase] getPrivacySettings error:", err);
    return null;
  }
}

/**
 * Get notification preferences for a user
 */
export async function getNotificationPreferences(
  userId: string,
): Promise<NotificationPreferences | null> {
  try {
    const { data, error } = await (
      supabase.from("notification_preferences") as any
    )
      .select("*")
      .eq("user_id", userId)
      .single();

    if (error) {
      // If no record exists, create default preferences
      if (error.code === "PGRST116") {
        const defaultPreferences: NotificationPreferences = {
          wellness_alerts: true,
          team_activity: true,
          daily_summary: false,
          break_reminders: true,
          quiet_hours_enabled: false,
        };

        const { data: newData, error: insertError } = await (
          supabase.from("notification_preferences") as any
        )
          .insert({
            user_id: userId,
            ...defaultPreferences,
          })
          .select()
          .single();

        if (insertError) {
          console.error(
            "[Supabase] Error creating default notification preferences:",
            insertError,
          );
          return null;
        }

        return newData as NotificationPreferences;
      }

      console.error(
        "[Supabase] Error fetching notification preferences:",
        error,
      );
      return null;
    }

    return data as NotificationPreferences;
  } catch (err) {
    console.error("[Supabase] getNotificationPreferences error:", err);
    return null;
  }
}

/**
 * Update notification preferences
 */
export async function updateNotificationPreferences(
  userId: string,
  preferences: Partial<NotificationPreferences>,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await (supabase.from("notification_preferences") as any)
      .update({
        ...preferences,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId);

    if (error) {
      console.error(
        "[Supabase] Error updating notification preferences:",
        error,
      );
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error("[Supabase] updateNotificationPreferences error:", err);
    return { success: false, error: err.message };
  }
}

/**
 * Request browser push notification permission
 */
export async function requestPushNotificationPermission(): Promise<{
  success: boolean;
  subscription?: PushSubscription;
  error?: string;
}> {
  try {
    if (!("Notification" in window)) {
      return { success: false, error: "Notifications not supported" };
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { success: false, error: "Permission denied" };
    }

    // Register service worker for push notifications
    if ("serviceWorker" in navigator && "PushManager" in window) {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: import.meta.env.VITE_VAPID_PUBLIC_KEY,
      });

      // Store subscription in database
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        await (supabase.from("push_subscriptions") as any).upsert({
          user_id: user.id,
          subscription: JSON.stringify(subscription),
          created_at: new Date().toISOString(),
        });
      }

      return { success: true, subscription };
    }

    return { success: false, error: "Push notifications not supported" };
  } catch (err: any) {
    console.error("[Supabase] Error requesting push permission:", err);
    return { success: false, error: err.message };
  }
}

/**
 * Get department breakdown with employee counts
 */
export async function getDepartmentBreakdown(): Promise<
  Array<{ department: string; count: number }>
> {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("department")
      .not("department", "is", null);

    if (error) {
      console.error("[Supabase] Error fetching department breakdown:", error);
      return [];
    }

    // Count employees per department
    const counts = new Map<string, number>();
    (data as Array<{ department: string | null }>)?.forEach((user) => {
      if (user.department) {
        counts.set(user.department, (counts.get(user.department) || 0) + 1);
      }
    });

    // Convert to array and sort by count
    return Array.from(counts.entries())
      .map(([department, count]) => ({ department, count }))
      .sort((a, b) => b.count - a.count);
  } catch (err) {
    console.error("[Supabase] getDepartmentBreakdown error:", err);
    return [];
  }
}

// ============================================================================
// EXPORT DATA
// ============================================================================

export interface ExportData {
  organization: OrganizationRow | null;
  users: UserRow[];
  wellnessLogs: WellnessLogRow[];
  alerts: WellnessAlertRow[];
}

export interface ExportProgress {
  stage: "users" | "logs" | "alerts" | "complete" | "idle";
  progress: number;
  total: number;
}

export async function exportOrganizationData(
  organizationId: string,
  onProgress?: (progress: ExportProgress) => void,
): Promise<ExportData | null> {
  try {
    onProgress?.({ stage: "users", progress: 0, total: 100 });

    const { data: organization } = await supabase
      .from("organizations")
      .select("*")
      .eq("id", organizationId)
      .single();

    onProgress?.({ stage: "users", progress: 30, total: 100 });

    const { data: users } = await supabase
      .from("users")
      .select("*")
      .eq("organization_id", organizationId);

    onProgress?.({ stage: "logs", progress: 50, total: 100 });

    const { data: wellnessLogs } = await supabase
      .from("wellness_logs")
      .select("*")
      .eq("organization_id", organizationId)
      .order("timestamp", { ascending: false });

    onProgress?.({ stage: "alerts", progress: 70, total: 100 });

    const { data: alerts } = await supabase
      .from("wellness_alerts")
      .select("*")
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false });

    onProgress?.({ stage: "complete", progress: 100, total: 100 });

    return {
      organization: organization as OrganizationRow | null,
      users: (users as UserRow[]) || [],
      wellnessLogs: (wellnessLogs as WellnessLogRow[]) || [],
      alerts: (alerts as WellnessAlertRow[]) || [],
    };
  } catch (err) {
    console.error("[Supabase] exportOrganizationData error:", err);
    return null;
  }
}

export function downloadExportData(
  data: ExportData,
  format: "json" | "csv" = "json",
): void {
  let content: string;
  let mimeType: string;
  let extension: string;

  if (format === "csv") {
    // For CSV, we primarily export the wellness logs as it's the most granular data
    // We flatten nested objects for better CSV compatibility
    const items = data.wellnessLogs;

    if (items.length === 0) {
      content = "No wellness logs found";
    } else {
      // Get headers from first item
      // We manually define headers to ensure order and handling of nested fields if needed
      // For now, we'll take all keys from the first log entry
      const headers = Object.keys(items[0]);

      const csvRows = [headers.join(",")];

      for (const row of items) {
        const values = headers.map((header) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const value = (row as any)[header];

          // Handle various value types
          if (value === null || value === undefined) {
            return "";
          } else if (typeof value === "object") {
            // Stringify objects (like details fields) and escape quotes
            return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
          } else if (typeof value === "string") {
            // Escape quotes and wrap in quotes if contains comma or quote
            const stringValue = value.replace(/"/g, '""');
            if (
              stringValue.includes(",") ||
              stringValue.includes('"') ||
              stringValue.includes("\n")
            ) {
              return `"${stringValue}"`;
            }
            return stringValue;
          } else {
            return String(value);
          }
        });
        csvRows.push(values.join(","));
      }

      content = csvRows.join("\n");
    }

    mimeType = "text/csv";
    extension = "csv";
  } else {
    // JSON Export - include everything structure
    const exportObj = {
      exported_at: new Date().toISOString(),
      organization: data.organization,
      users: data.users,
      wellness_logs: data.wellnessLogs,
      alerts: data.alerts,
    };
    content = JSON.stringify(exportObj, null, 2);
    mimeType = "application/json";
    extension = "json";
  }

  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `halo-export-${new Date().toISOString().split("T")[0]}.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
